import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router';
import type {
  EmployerVerificationCheckpoint,
  EmployerVerificationDetail,
} from '../../api/employerVerificationDashboardApi';
import { employerVerificationFinalChipColor } from '../../lib/employerVerificationFinalChip';
import {
  finalOutcomeLabelStored,
  finalOutcomeTechnical,
  gateCodeToTitle,
  gateMeta,
  GATE_ORDER_PIPE,
  type GateKey,
  verificationDetailHints,
} from '../../lib/employerVerificationCopy';
import {
  formatGateStepLabel,
  findFirstFailedPersistedCheckpoint,
} from '../../lib/employerVerificationGateAudit';
import { GateCheckpointAuditSection } from './GateCheckpointAuditSection';
import { GateStatusOverviewSection } from './GateStatusOverviewSection';
import { findFirstFailingGateKey, GatePipelineStrip } from './GatePipelineStrip';
import { formatGateReasonReadableFlow } from '../../lib/employerVerificationGateReasonFormat';
import { extractReviewSignalBundle } from '../../lib/employment';
import { EmployerAttemptDetailAccordion } from './EmployerAttemptDetailAccordion';

function checkpointGateKey(cp: EmployerVerificationCheckpoint): GateKey | null {
  const u = cp.gate.trim().toUpperCase();
  return (GATE_ORDER_PIPE as readonly string[]).includes(u) ? (u as GateKey) : null;
}

export type EmployerVerificationDetailLayout = 'drawer' | 'fullscreen';

export type EmployerVerificationDetailPaneProps = {
  data: EmployerVerificationDetail;
  layout: EmployerVerificationDetailLayout;
  /** When true, renders only inner content (no drawer header / fullscreen app bar). For embedding under Applications. */
  embedded?: boolean;
  focusedGate?: GateKey | null;
  onClose: () => void;
  onRequestFullscreen?: () => void;
};

export function EmployerVerificationDetailPane({
  data,
  layout,
  embedded = false,
  focusedGate,
  onClose,
  onRequestFullscreen,
}: EmployerVerificationDetailPaneProps) {
  const theme = useTheme();
  const firstFail = findFirstFailingGateKey(data.gates);
  const persistedFail = findFirstFailedPersistedCheckpoint(data.gateCheckpointAudit ?? null);
  const showExpand = !embedded && layout === 'drawer' && typeof onRequestFullscreen === 'function';
  const reviewBundle = extractReviewSignalBundle({ evidence: data.evidence });

  const monospace = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: '0.8125rem',
    lineHeight: 1.6,
    fontWeight: 500,
    wordBreak: 'break-word' as const,
  };

  const content = (
    <Box sx={{ px: layout === 'fullscreen' ? { xs: 2, sm: 3 } : 2, pb: layout === 'fullscreen' ? 4 : 3 }}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Tooltip title={`Stored value: ${finalOutcomeTechnical(data.finalStatus)}`} placement="bottom" arrow>
            <Chip
              size="small"
              label={finalOutcomeLabelStored(data.finalStatus)}
              color={employerVerificationFinalChipColor(data.finalStatus)}
              sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
            />
          </Tooltip>
          <Button
            component={RouterLink}
            to={`/employer-verification/company/${encodeURIComponent(data.id)}`}
            variant="contained"
            size="small"
          >
            Open company dossier
          </Button>
          {showExpand ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInFullRoundedIcon />}
              onClick={onRequestFullscreen}
            >
              Full screen
            </Button>
          ) : null}
        </Stack>

        {persistedFail ? (
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Blocking step (persisted): {gateMeta[persistedFail.gate].title} ·{' '}
              {formatGateStepLabel(persistedFail.step)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.55 }}>
              {persistedFail.reason}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Same wording appears under “Checkpoint detail” below—no guesswork between gate label and verifier text.
            </Typography>
          </Alert>
        ) : firstFail ? (
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              First gate that blocked approval: {gateMeta[firstFail].title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Step-level audit is not stored for this legacy run—use gate summary text and stored fail reason below.
            </Typography>
          </Alert>
        ) : null}

        {data.finalStatus?.trim().toUpperCase() === 'REJECTED' && !firstFail ? (
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
            No gate registered a FAIL in stored results — rejection may originate outside the checkpoint row. Use JSON and
            service logs for lineage.
          </Alert>
        ) : null}

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
            Checkpoint flow
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {verificationDetailHints.gateFlowLead}
          </Typography>
          <GatePipelineStrip
            gates={data.gates}
            gateCheckpointAudit={data.gateCheckpointAudit ?? null}
            density="comfortable"
            pinFirstFailureKey={firstFail}
          />
        </Box>

        <GateStatusOverviewSection
          gates={data.gates}
          gateMessages={data.gateMessages}
          gateCheckpointAudit={data.gateCheckpointAudit ?? null}
        />

        <GateCheckpointAuditSection audit={data.gateCheckpointAudit ?? null} focusedGate={focusedGate ?? null} />

        <Divider />

        <Stack spacing={1}>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>
              Application
            </Box>{' '}
            <Box component="span" sx={monospace}>
              {data.applicationId}
            </Box>
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>
              Applicant
            </Box>{' '}
            <Box component="span" sx={monospace}>
              {data.applicantId}
            </Box>
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>
              Customer ID
            </Box>{' '}
            <Box component="span" sx={monospace}>
              {data.ccid ?? 'Not on file'}
            </Box>
          </Typography>
          {data.loanApplication.applicantEmail ? (
            <Typography variant="body2" color="text.secondary">
              Orchestrator-linked email when configured:{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{data.loanApplication.applicantEmail}</Box>
            </Typography>
          ) : null}
        </Stack>

        {data.failReason ? (
          <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Why underwriting stopped short
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              {data.failReason}
            </Typography>
          </Alert>
        ) : null}
      </Stack>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
        Gate summaries & applicant snapshot
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Narrative field from the dashboard API (includes applicant-submitted employment snapshot where available). Prefer
        “Checkpoint detail” above for verifier line items on new runs.
      </Typography>
      {data.checkpoints.map((cp) => {
        const key = checkpointGateKey(cp);
        const gateStored = key ? ((data.gates[key] ?? '') as string) : '';
        const u = gateStored.trim().toUpperCase();
        const failAccent = u === 'FAIL';

        return (
          <Accordion
            key={`${data.id}-${cp.gate}`}
            disableGutters
            defaultExpanded={
              (!!focusedGate && focusedGate === key) ||
              (!!firstFail && firstFail === key) ||
              (!!persistedFail && persistedFail.gate === key)
            }
            sx={{
              '&:before': { display: 'none' },
              border: 1,
              borderColor: failAccent ? alpha(theme.palette.error.main, 0.45) : 'divider',
              borderLeftWidth: failAccent ? 4 : 1,
              borderLeftStyle: 'solid',
              borderLeftColor: failAccent ? theme.palette.error.main : undefined,
              borderRadius: 1.5,
              mb: 1,
              '&.Mui-expanded': { mb: 1 },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 1 } }}>
              <Stack spacing={0.25} sx={{ mr: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  {gateCodeToTitle(cp.gate)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Stored status:&nbsp;<strong>{cp.statusLabel}</strong>
                  {failAccent ? ' — review this passage before JSON' : null}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.65, wordBreak: 'break-word', whiteSpace: 'pre-line' }}
              >
                {formatGateReasonReadableFlow(cp.adminSummary)}
              </Typography>
              {data.gateMessages[cp.gate] ? (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.text.primary, 0.035),
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.02, mb: 0.5 }}
                  >
                    Stored gate message
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.8125rem',
                      lineHeight: 1.65,
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {formatGateReasonReadableFlow(data.gateMessages[cp.gate])}
                  </Typography>
                </Box>
              ) : null}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {data.fraudSignals.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Fraud / review signals
          </Typography>
          {data.fraudSignals.map((f) => (
            <Alert key={f.id} severity="info" sx={{ mb: 1, borderRadius: 2 }} variant="outlined">
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {f.signalName}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
                Risk weight&nbsp;{f.riskContribution}
              </Typography>
              <Typography variant="caption" component="pre" sx={{ ...monospace, m: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(f.observationData, null, 2)}
              </Typography>
            </Alert>
          ))}
        </Box>
      ) : null}

      <Stack spacing={1.25} sx={{ mt: 3 }}>
        <EmployerAttemptDetailAccordion
          row={data as unknown as Record<string, unknown>}
          evidence={data.evidence}
          bundle={reviewBundle}
          isLatest
          defaultExpanded
        />
      </Stack>

      <Accordion sx={{ mt: 3, '&:before': { display: 'none' }, borderRadius: 2 }} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
          <Stack spacing={0.25}>
            <Typography sx={{ fontWeight: 700 }}>Raw JSON — signals & evidence bundle</Typography>
            <Typography variant="caption" color="text.secondary">
              {verificationDetailHints.rawJsonLead}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {firstFail ? (
            <Alert severity="warning" variant="filled" sx={{ mb: 1.5, borderRadius: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.12), color: 'text.primary' }}>
              JSON path hints: search for&nbsp;
              <Box component="span" sx={{ fontWeight: 800 }}>
                "{firstFail}"
              </Box>
              {' '}or&nbsp;
              <Box component="span" sx={{ fontWeight: 800 }}>
                FAIL
              </Box>
              &nbsp;to jump to the first blocking gate signals.
            </Alert>
          ) : null}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1.25,
              maxHeight: layout === 'fullscreen' ? 'min(70vh, 720px)' : 420,
              overflow: 'auto',
              bgcolor: alpha(theme.palette.info.main, 0.045),
              border: '1px solid',
              borderColor: alpha(theme.palette.divider, 0.9),
              boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.black, theme.palette.mode === 'light' ? 0.04 : 0.2)}`,
            }}
          >
            <Typography
              component="pre"
              sx={{
                m: 0,
                ...monospace,
                fontFeatureSettings: '"liga" 0, "calt" 0',
                letterSpacing: 0,
              }}
            >
              {JSON.stringify({ reviewSignals: data.reviewSignals, evidence: data.evidence }, null, 2)}
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  if (embedded) {
    return <Box sx={{ py: 0.5 }}>{content}</Box>;
  }

  if (layout === 'fullscreen') {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
        <AppBar
          position="sticky"
          elevation={0}
          color="default"
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <Typography sx={{ flex: 1, fontWeight: 800 }} variant="subtitle1">
              Verification record
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 360 }}>
              ID:&nbsp;<Box component="span" sx={monospace}>{data.id}</Box>
            </Typography>
            <IconButton aria-label="Close full screen detail" edge="end" onClick={onClose} color="inherit">
              <CloseRoundedIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        {content}
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, px: 2 }}>
        <Stack spacing={0.25} sx={{ pt: 0.25 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Verification record
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Checkpoint stories + downloadable JSON&nbsp;· ID{' '}
            <Box component="span" sx={monospace}>
              {data.id}
            </Box>
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ mt: -0.5 }}>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
      {content}
    </Stack>
  );
}
